import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X, GraduationCap, Building2, Tag, Loader2 } from 'lucide-react';
import { getAcademySuggesties } from '../../services/medewerkerApi';
import type { AcademySuggesties } from '../../services/medewerkerApi';
import { LEERVORM } from './academyStijl';

const MIN_TEKENS = 3;
const VERTRAGING_MS = 220;

function leervormLabel(soort: string | null): string {
  const s = (soort ?? '').toLowerCase();
  if (/e-?learning|online|on-?demand/.test(s)) return LEERVORM.e_learning.label;
  if (/meerdaags|opleiding|cursus|traject/.test(s)) return LEERVORM.opleiding.label;
  return LEERVORM.workshop.label;
}

type Item =
  | { soort: 'cursus'; id: number }
  | { soort: 'opleider'; id: number }
  | { soort: 'thema'; hoofdgroep: string; subcategorie_slug: string | null };

/**
 * Fuzzy text search-balk met realtime asynchrone suggesties (vanaf 3 tekens):
 * een vederlichte, lichte dropdown met de secties Cursussen, Opleiders en Thema's.
 */
export const AcademyZoekbalk: React.FC<{
  waarde: string;
  onChange: (v: string) => void;
  onKiesCursus: (id: number) => void;
  onKiesOpleider: (id: number) => void;
  onKiesThema: (hoofdgroep: string, subcategorieSlug: string | null) => void;
}> = ({ waarde, onChange, onKiesCursus, onKiesOpleider, onKiesThema }) => {
  const { t } = useTranslation('medewerker');
  const [open, setOpen] = useState(false);
  const [laden, setLaden] = useState(false);
  const [suggesties, setSuggesties] = useState<AcademySuggesties | null>(null);
  const [actief, setActief] = useState(-1);
  const wrapper = useRef<HTMLDivElement>(null);
  const listId = useId();

  // Live suggesties: debounce + annuleren van verouderde verzoeken.
  useEffect(() => {
    const q = waarde.trim();
    if (q.length < MIN_TEKENS) {
      setSuggesties(null);
      setLaden(false);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setLaden(true);
      try {
        const data = await getAcademySuggesties(q, controller.signal);
        if (!controller.signal.aborted) {
          setSuggesties(data);
          setActief(-1);
        }
      } catch {
        if (!controller.signal.aborted) setSuggesties(null);
      } finally {
        if (!controller.signal.aborted) setLaden(false);
      }
    }, VERTRAGING_MS);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [waarde]);

  useEffect(() => {
    const sluit = (e: MouseEvent) => {
      if (wrapper.current && !wrapper.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', sluit);
    return () => document.removeEventListener('mousedown', sluit);
  }, []);

  const items: Item[] = suggesties
    ? [
        ...suggesties.cursussen.map((c): Item => ({ soort: 'cursus', id: c.id })),
        ...suggesties.opleiders.map((o): Item => ({ soort: 'opleider', id: o.id })),
        ...suggesties.themas.map((th): Item => ({ soort: 'thema', hoofdgroep: th.hoofdgroep, subcategorie_slug: th.subcategorie_slug })),
      ]
    : [];
  const heeftSuggesties = items.length > 0;
  const toonDropdown = open && waarde.trim().length >= MIN_TEKENS && (heeftSuggesties || laden);

  const kies = (item: Item) => {
    setOpen(false);
    if (item.soort === 'cursus') onKiesCursus(item.id);
    else if (item.soort === 'opleider') onKiesOpleider(item.id);
    else onKiesThema(item.hoofdgroep, item.subcategorie_slug);
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!toonDropdown || !heeftSuggesties) {
      if (e.key === 'Escape') setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActief((i) => (i + 1) % items.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActief((i) => (i <= 0 ? items.length - 1 : i - 1));
    } else if (e.key === 'Enter' && actief >= 0) {
      e.preventDefault();
      kies(items[actief]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  let index = -1;
  const rij = (item: Item, inhoud: React.ReactNode, key: string) => {
    index += 1;
    const i = index;
    return (
      <li key={key} role="option" aria-selected={actief === i} id={`${listId}-${i}`}>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => kies(item)}
          onMouseEnter={() => setActief(i)}
          className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition ${actief === i ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-50'}`}
        >
          {inhoud}
        </button>
      </li>
    );
  };

  const placeholder = t('academy.zoek.placeholder', { defaultValue: 'Zoek op trefwoord, onderwerp of opleider (bijv. ChatGPT, Excel)…' });

  return (
    <div ref={wrapper} className="relative">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
      <input
        type="search"
        role="combobox"
        aria-expanded={toonDropdown}
        aria-controls={listId}
        aria-activedescendant={actief >= 0 ? `${listId}-${actief}` : undefined}
        aria-autocomplete="list"
        value={waarde}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder={placeholder}
        aria-label={placeholder}
        autoComplete="off"
        className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-9 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#0B1F3A] focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/15"
      />
      <span className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-1">
        {laden && <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-hidden="true" />}
        {waarde && (
          <button type="button" onClick={() => onChange('')} className="text-gray-400 hover:text-gray-900" aria-label={t('algemeen.wissen', { defaultValue: 'Wissen' })}>
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </span>

      {toonDropdown && (
        <ul
          id={listId}
          role="listbox"
          className="absolute left-0 right-0 top-full z-30 mt-2 max-h-[24rem] overflow-y-auto rounded-2xl border border-gray-200 bg-white py-2 shadow-xl"
        >
          {suggesties && suggesties.cursussen.length > 0 && (
            <li role="presentation" className="px-4 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              {t('academy.zoek.cursussen', { defaultValue: 'Cursussen' })}
            </li>
          )}
          {suggesties?.cursussen.map((c) =>
            rij(
              { soort: 'cursus', id: c.id },
              <>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-lg" aria-hidden="true">
                  {c.emoji ?? <GraduationCap className="h-4 w-4 text-gray-400" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{c.titel}</span>
                  <span className="block truncate text-xs text-gray-500">
                    {leervormLabel(c.soort_opleiding)} · {c.organisatie_naam}
                  </span>
                </span>
              </>,
              `c-${c.id}`
            )
          )}
          {suggesties && suggesties.opleiders.length > 0 && (
            <li role="presentation" className="mt-1 border-t border-gray-100 px-4 pb-1 pt-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              {t('academy.zoek.opleiders', { defaultValue: 'Opleiders' })}
            </li>
          )}
          {suggesties?.opleiders.map((o) =>
            rij(
              { soort: 'opleider', id: o.id },
              <>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-100" aria-hidden="true">
                  {o.logo_url ? <img src={o.logo_url} alt="" className="h-full w-full object-cover" /> : <Building2 className="h-4 w-4 text-gray-400" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{o.naam}</span>
                  <span className="block text-xs text-gray-500">{t('academy.zoek.opleiderProfiel', { defaultValue: 'Opleidersprofiel · {{n}} cursussen', n: o.aantal })}</span>
                </span>
              </>,
              `o-${o.id}`
            )
          )}
          {suggesties && suggesties.themas.length > 0 && (
            <li role="presentation" className="mt-1 border-t border-gray-100 px-4 pb-1 pt-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              {t('academy.zoek.themas', { defaultValue: "Thema's" })}
            </li>
          )}
          {suggesties?.themas.map((th) =>
            rij(
              { soort: 'thema', hoofdgroep: th.hoofdgroep, subcategorie_slug: th.subcategorie_slug },
              <>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-lg" aria-hidden="true">
                  {th.emoji ?? <Tag className="h-4 w-4 text-gray-400" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{th.naam ?? t(`academy.hoofdgroep.${th.hoofdgroep}`, { defaultValue: th.hoofdgroep })}</span>
                  <span className="block text-xs text-gray-500">{t('academy.zoek.themaBekijken', { defaultValue: 'Alle cursussen in dit thema' })}</span>
                </span>
              </>,
              `t-${th.hoofdgroep}-${th.subcategorie_slug ?? ''}`
            )
          )}
        </ul>
      )}
    </div>
  );
};

export default AcademyZoekbalk;
